import { useEffect } from 'react';

/**
 * Custom hook to update meta tags
 * Workaround for react-helmet-async not updating meta tags properly
 */
const useMetaTags = ({ title, description, ogTitle, ogDescription, canonical, ogImage, ogType = 'website' }) => {
  useEffect(() => {
    // Update title
    if (title) {
      document.title = title;
    }

    // Helper function to update or create meta tag
    const updateMeta = (selector, content, attribute = 'content') => {
      if (!content) return;
      
      let element = document.querySelector(selector);
      if (element) {
        element.setAttribute(attribute, content);
      } else {
        // Create new meta tag if it doesn't exist
        element = document.createElement('meta');
        if (selector.includes('property=')) {
          const property = selector.match(/property="([^"]+)"/)?.[1];
          if (property) {
            element.setAttribute('property', property);
          }
        } else if (selector.includes('name=')) {
          const name = selector.match(/name="([^"]+)"/)?.[1];
          if (name) {
            element.setAttribute('name', name);
          }
        }
        element.setAttribute(attribute, content);
        document.head.appendChild(element);
      }
    };

    // Helper function to update or create link tag
    const updateLink = (rel, href) => {
      if (!href) return;
      
      let element = document.querySelector(`link[rel="${rel}"]`);
      if (element) {
        element.setAttribute('href', href);
      } else {
        element = document.createElement('link');
        element.setAttribute('rel', rel);
        element.setAttribute('href', href);
        document.head.appendChild(element);
      }
    };

    // Update meta description
    updateMeta('meta[name="description"]', description);
    
    // Update Open Graph tags
    updateMeta('meta[property="og:title"]', ogTitle || title);
    updateMeta('meta[property="og:description"]', ogDescription || description);
    updateMeta('meta[property="og:type"]', ogType);
    if (ogImage) {
      updateMeta('meta[property="og:image"]', ogImage);
    }
    if (canonical) {
      updateMeta('meta[property="og:url"]', canonical);
    }
    
    // Update Twitter Card tags
    updateMeta('meta[name="twitter:title"]', ogTitle || title);
    updateMeta('meta[name="twitter:description"]', ogDescription || description);
    if (ogImage) {
      updateMeta('meta[name="twitter:image"]', ogImage);
    }
    
    // Update canonical link
    updateLink('canonical', canonical);

  }, [title, description, ogTitle, ogDescription, canonical, ogImage, ogType]);
};

export default useMetaTags;
